import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import ReactDOMServer from 'react-dom/server';
import { RecoilRoot } from 'recoil';
import { GoogleMapAPIProvider } from '@Components/Utilities/GoogleMapAPIProvider'; 
import loadable, { loadableReady } from '@loadable/component';
import { ChunkExtractor } from '@loadable/server';
import { WindowSizeProvider } from '@Components/Utilities/WindowSize';
import { bannerAdPositions } from '@Components/Utilities/SandhillsConstants';
import { getBannerAd } from '@Components/Utilities/SandhillsUtility';
import { getConsentCookie, setConsentChanged, setConsentCookie, setConsentModeSettings } from '@Components/Utilities/CookieHelper';
import atoms from '@Components/Utilities/SandhillsAtoms';
import DoubleClickAd from '@Components/Shared/DoubleClick/DoubleClickAd';
import ScrollToTop from '@Components/Shared/ScrollToTop';
import UnsupportedBrowserModal from '@Components/Shared/Modals/UnsupportedBrowserModal';
import AccessibilitySkipNav from '@Components/Shared/AccessibilitySkipNav';
import createEmotionServer from '@emotion/server/create-instance';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { createTheme } from '@TSReact/Theme';
import 'intersection-observer'; // Needed for Polyfill support for iOS < 12.2
import { FirebaseProvider } from '@Components/Utilities/Firebase'; 

const App = (props) => {

    const AsyncComp = loadable(compProps => import(/* webpackExclude: /(App|Theme).jsx$/i */ `./${compProps.Path}/${compProps.Name}.jsx`));
    const siteSettings = { ...props.SiteSettings };

    sandhills.TradeSites.React.SiteSettings = siteSettings;
    sandhills.TradeSites.React.ComponentProps = {};


    if (typeof window !== 'undefined') {
        if (!window.Overrides) window.Overrides = {};
        if (siteSettings.IsHostedSite) {
            window.Overrides = {
                ...window.Overrides,
                baseUrl: siteSettings.HostedSiteBaseUrl,
                isHostedSite: siteSettings.IsHostedSite
            };
        } else {
            window.Overrides = {
                ...window.Overrides,
                baseUrl: ''
            };
        }
    }
    if (props.CurrentUser?.LanguageCultureCode) {
        sandhills.TradeSites.React.ComponentProps['langCode'] = props.CurrentUser.LanguageCultureCode;
    }
    if (props.CurrentUser?.LanguageID) {
        sandhills.TradeSites.React.ComponentProps['langID'] = props.CurrentUser.LanguageID;
    }
    if (props.CurrentWebSiteModel?.CurrentSite) {
        sandhills.TradeSites.React.ComponentProps['siteName'] = props.CurrentWebSiteModel.CurrentSite;
    }
    if (props.CurrentWebSiteModel?.TopLevelDomain) {
        sandhills.TradeSites.React.ComponentProps['topLevelDomain'] = props.CurrentWebSiteModel.TopLevelDomain;
    }
    if (props.CurrentWebSiteModel?.RequireOptInConsent) {
        sandhills.TradeSites.React.ComponentProps['requireOptInConsent'] = props.CurrentWebSiteModel.RequireOptInConsent;
    }
    if (props.CurrentWebSiteModel?.UseUserCentricsCMP) {
        sandhills.TradeSites.React.ComponentProps['useUserCentricsCMP'] = props.CurrentWebSiteModel.UseUserCentricsCMP;
    }
    if (props.DefaultSiteLangCode) {
        sandhills.TradeSites.React.ComponentProps['defaultSiteLangCode'] = props.DefaultSiteLangCode;
    }
    if (props.HasLanguageSelector) {
        sandhills.TradeSites.React.ComponentProps['hasLangSelector'] = props.HasLanguageSelector;
    }
    if (props.HasCountrySelector) {
        sandhills.TradeSites.React.ComponentProps['hasCountrySelector'] = props.HasCountrySelector;
        sandhills.TradeSites.React.ComponentProps['countryCode'] = props.CountryCode;
    }
    if (props.SandhillsCookieClassifications) {
        sandhills.TradeSites.React.ComponentProps['sandhillsCookieClassifications'] = props.SandhillsCookieClassifications;
    }
    if (props.ShowEmployeeOnlyBetaSiteToggle) {
        sandhills.TradeSites.React.ComponentProps['showEmployeeOnlyBetaSiteToggle'] = props.ShowEmployeeOnlyBetaSiteToggle;
    }
    if (props.CurrentUser.UserAuthenticatedTrackingID) {
        sandhills.TradeSites.React.ComponentProps['userAuthenticatedTrackingID'] = props.CurrentUser.UserAuthenticatedTrackingID;
    }
    if (props.CurrentUser.IsAuthenticated) {
        sandhills.TradeSites.React.ComponentProps['userIsAuthenticated'] = props.CurrentUser.IsAuthenticated;
    }
    if (props.CurrentUser?.IsIOSDevice) {
        sandhills.TradeSites.React.ComponentProps['isIOSDevice'] = props.CurrentUser.IsIOSDevice;
    }

    useEffect(() => {
        //Setting up trigger for Google ConsentMode Template.
        //This logic is being ran after all componenets have been loaded, do not move to component.
        var consent = getConsentCookie(!props.CurrentUser?.IsAuthenticated);
        if (!props.CurrentWebSiteModel?.UseUserCentricsCMP) {
            window.onSandhillsConsentUpdate = function (callBack) {
                if (props.CurrentWebSiteModel?.RequireOptInConsent) {
                    consentListeners.push(callBack);

                    //Run ConsentUpdate on page load if user has previously authorized consents.  
                    if (consent && (consent.Advertising || consent.Analytics || consent.Customization)) {
                        setConsentModeSettings(consent);
                    }
                }
            }

            setConsentCookie();
        }
        setConsentChanged(consent);        
    }, []);

    const topPopUpAd = getBannerAd(props.BodyComponent?.Props?.DoubleClickAdCollection?.AdUnits, bannerAdPositions.TopPopUp);
    const themeOptions = createTheme(siteSettings.HostedSiteMuiTheme);
    const valueInsightPortal = props.AdditionalComponents.find(x => x.Name == 'ValueInsightPortalButton');
    if (valueInsightPortal) {
        sandhills.TradeSites.React.ComponentProps['vip'] = valueInsightPortal?.Props;
    }

    const initializeState = ({ set }) => {
        set(atoms.currentUser, props.CurrentUser);
        set(atoms.showCookieConsent, props.DisplayCookieConsentModal);
        set(atoms.showQsu, false);
        set(atoms.showRaq, false);
        set(atoms.isModalOpen, false);
        set(atoms.showLae, false);
        set(atoms.lae, '');
        set(atoms.defaultSiteLangCode, props.DefaultSiteLangCode);
        set(atoms.sitePhoneFormatCultureCode, props.SitePhoneFormatCultureCode);
        set(atoms.hasLanguageSelector, props.HasLanguageSelector);
        set(atoms.hasCountrySelector, props.hasCountrySelector);
        set(atoms.countryCode, props.countryCode);
        set(atoms.isInTranslationEditor, props.IsInTranslationEditorMode)
        set(atoms.currentWebsite, props.CurrentWebSiteModel);
        set(atoms.routes, props.Routes);
        set(atoms.muiThemes, themeOptions);
        set(atoms.showEmployeeOnlyBetaSiteToggle, props.ShowEmployeeOnlyBetaSiteToggle);
        set(atoms.isAuctionResult, props.BodyComponent?.Props?.IsAuctionResult);
    };

    return (
        <CacheProvider value={emotionCache}>
            <ThemeProvider theme={themeOptions.mainMode == "dark" ? themeOptions.darkTheme : themeOptions.lightTheme}>
                <GoogleMapAPIProvider googleMapsApiKey={props.GoogleMapAPIKey}>
                    <input type='hidden' id='isMobileAppContext' value={props.CurrentWebSiteModel.IsMobileAppContext} />
                    <CssBaseline />
                    <RecoilRoot initializeState={initializeState}>
                        <WindowSizeProvider>
                            <FirebaseProvider firebaseConfig={props.FirebaseConfig} vapidKey={props.FirebaseVapidKey}>
                            {topPopUpAd &&
                                <DoubleClickAd
                                    {...props.BodyComponent?.Props?.DoubleClickAdCollection}
                                    position={bannerAdPositions.TopPopUp}
                                    ad={topPopUpAd}
                                />}
                            <div className='body-wrapper'>
                                {!siteSettings.IsHostedSite &&
                                    <AccessibilitySkipNav />}
                                {props.HeaderComponent && !(props.HideHeaderAndFooter || props.HideHeader) && <AsyncComp Name={props.HeaderComponent.Name} Path={props.HeaderComponent.Path} loginPromptEventId={props.BodyComponent?.Props?.LoginPromptEventId} pageName={props.BodyComponent?.Name} siteName={props.CurrentWebSiteModel?.CurrentSite} IsAuctionResults={props?.BodyComponent?.Props?.IsAuctionResult} showLoginPromptSiteSetting={props.SiteSettings?.ShowLoginPrompt} {...props.HeaderComponent.Props} />}
                                {props.BodyComponent && <AsyncComp Name={props.BodyComponent?.Name} Path={props.BodyComponent?.Path}  {...props.BodyComponent?.Props} />}
                            </div>
                            {!props.CurrentWebSiteModel.IsMobileAppContext && !props.HideHeaderAndFooter && props.FooterComponent && <AsyncComp Name={props.FooterComponent.Name} Path={props.FooterComponent.Path}  {...props.FooterComponent.Props} />}
                            {props.AdditionalComponents && props.AdditionalComponents.map(additionalComp => <AsyncComp key={additionalComp.Name} Name={additionalComp.Name} Path={additionalComp.Path} {...additionalComp.Props} />)}
                            {!props.CurrentWebSiteModel.IsMobileAppContext && <ScrollToTop />}
                            <UnsupportedBrowserModal />
                            {siteSettings.IsHostedSite && themeOptions.mainMode == "dark" &&
                                <style>
                                    {`
                                /* Temp Patch for Dark Theme HS by setting everything that doesnt meet these rules to have white text */
                                .detail-wrapper :not([class*="Mui"], [class*="Mui"] *,
                                .detail-shipping, .detail-shipping *,
                                .detail__specs-label, .detail__specs-label *,
                                .inspection__spec-label, .inspection__spec-label *, 
                                .detail__beta-site-warning, .detail__beta-site-warning *,
                                .currency-select, .currency-selector-option,
                                .mc-media-overlay-banner-span, .mc-media-overlay-banner-span *
                                )
                                {
                                    color: #ffffff;
                                }
                                .print-detail-wrapper :not([class*="Mui"], [class*="Mui"] *,
                                .detail-shipping, .detail-shipping *,
                                .detail__specs-label, .detail__specs-label *,
                                .inspection__spec-label, .inspection__spec-label *, 
                                .detail__beta-site-warning, .detail__beta-site-warning *,
                                .currency-select, .currency-selector-option
                                )
                                {
                                    color: #ffffff;
                                }
                                `}
                                    </style>}
                            </FirebaseProvider>
                        </WindowSizeProvider>
                    </RecoilRoot>
                </GoogleMapAPIProvider>
            </ThemeProvider>
        </CacheProvider>
    );
};

global.App = App;

/* Needed for Server Side Rendering */
global.React = React;
global.ReactDOM = ReactDOM;
global.ReactDOMServer = ReactDOMServer;
global.loadableReady = loadableReady;
global.ChunkExtractor = ChunkExtractor;
global.createEmotionServer = createEmotionServer;
global.createCache = createCache;

__webpack_get_script_filename__ = (chunkId) => sandhills.TradeSites.React.ChunkBundles[chunkId.toLowerCase()];
__webpack_require__.miniCssF = (chunkId) => sandhills.TradeSites.React.ChunkBundles[`styles/${chunkId.toLowerCase()}`];